import { FC, useCallback, useState } from "react";

import { OrderStep } from "../../constants";
import type { TOrderDataContext, TStaticInitialOrderData } from "../../types";
import useDefaultUserAddress from "../hooks/useDefaultUserAddress";
import useDynamicData from "../hooks/useDynamicData";
import useEditShipment from "../hooks/useEditShipment";
import useInitialValues from "../hooks/useInitialValues";
import useInvoiceAddresses from "../hooks/useInvoiceAddresses";
import useIsInitialDataLoaded from "../hooks/useIsInitialDataLoaded";
import useOrderShipment from "../hooks/useOrderShipment";
import usePrefillData from "../hooks/usePrefillData";
import useStaticData from "../hooks/useStaticData";
import useSteps from "../hooks/useSteps";
import OrderDataContext from "../OrderDataContext";

export type TOrderContextProviderProps = {
  currentStep: OrderStep;
  staticOrderData?: TStaticInitialOrderData;
  shipmentId?: string;
  weightRegulations: string[] | null;
  children?: any;
};

const OrderDataContextProvider: FC<TOrderContextProviderProps> = ({
  currentStep,
  staticOrderData,
  shipmentId,
  weightRegulations,
  children,
}) => {
  const { loadedInitialData, setInitialDataPartLoaded } = useIsInitialDataLoaded();

  const staticData = useStaticData(staticOrderData);
  const { dynamicData, updateCountriesTo, updatePackageSizes, updateOffers, updatePickupTimeFrames } =
    useDynamicData(staticOrderData);

  const initialValues = useInitialValues(currentStep, weightRegulations, staticOrderData);

  const { steps, updateSteps } = useSteps();
  const { address: defaultUserAddress, isLoadingAddresses } = useDefaultUserAddress();
  const invoiceAddresses = useInvoiceAddresses();
  const prefillData = usePrefillData();

  const { isLoading: isShipmentSaveLoading, saveShipment } = useOrderShipment();

  const { isLoading: isShipmentEditLoading, getShipmentEditInfo } = useEditShipment();

  const isEdit = !!shipmentId;

  const getShipmentEditData = useCallback(() => getShipmentEditInfo(shipmentId), [getShipmentEditInfo, shipmentId]);

  const [georoutingErrors, setGeoroutingErrors] = useState<Record<string, string>>({});

  const setGeoroutingError = useCallback((field: string, error: string) => {
    setGeoroutingErrors(prev => ({ ...prev, [field]: error }));
  }, []);

  const clearGeoroutingError = useCallback((field: string) => {
    setGeoroutingErrors(prev => {
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const getGeoroutingErrors = useCallback(() => georoutingErrors, [georoutingErrors]);

  const value: TOrderDataContext = {
    loadedInitialData,
    setInitialDataPartLoaded,
    isEdit,
    staticData,
    dynamicData,
    initialValues,
    defaultUserAddress,
    invoiceAddresses,
    prefillData,
    steps,
    updateCountriesTo,
    updatePackageSizes,
    updateOffers,
    updatePickupTimeFrames,
    updateSteps,
    isShipmentSaveLoading,
    isLoadingAddresses,
    saveShipment,
    isShipmentEditLoading,
    getShipmentEditData,
    weightRegulations,
    getGeoroutingErrors,
    setGeoroutingError,
    clearGeoroutingError,
  };

  return <OrderDataContext.Provider value={value}>{children}</OrderDataContext.Provider>;
};
export default OrderDataContextProvider;
